/**
 * Date Validation and Sanitization Utilities
 *
 * CRITICAL: This module prevents RangeError: date value is not finite in DateTimeFormat
 * by providing bulletproof date validation and formatting functions
 */

/**
 * Validates if a date is a valid, finite date object
 */
export function isValidDate(date: any): date is Date {
  return date instanceof Date && !isNaN(date.getTime()) && isFinite(date.getTime());
}

/**
 * Validates if a date string or value can be safely converted to a Date
 */
export function isValidDateValue(value: any): boolean {
  if (value === null || value === undefined || value === '') {
    return false;
  }

  if (typeof value === 'string') {
    // Check for obviously invalid date strings
    if (value.trim() === '' || value === 'null' || value === 'undefined') {
      return false;
    }
  }

  const date = new Date(value);
  return isValidDate(date);
}

/**
 * Safely creates a Date object with validation
 * Returns null if the input cannot be converted to a valid date
 */
export function createSafeDate(value: any): Date | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (isValidDate(value)) {
    return value;
  }

  try {
    const date = new Date(value);
    return isValidDate(date) ? date : null;
  } catch (error) {
    console.warn('Failed to create date from value:', value, error);
    return null;
  }
}

/**
 * Creates a Date object with a fallback to current date
 * NEVER returns null - always provides a valid date
 */
export function createDateWithFallback(value: any, fallback: Date = new Date()): Date {
  const safeDate = createSafeDate(value);
  return safeDate || fallback;
}

/**
 * Safely formats a date with comprehensive error handling
 * Returns a fallback string if formatting fails
 */
export function safeDateFormat(
  date: any,
  options: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  },
  fallback: string = 'Date unavailable'
): string {
  try {
    const validDate = createSafeDate(date);
    if (!validDate) {
      return fallback;
    }

    return new Intl.DateTimeFormat('en-US', options).format(validDate);
  } catch (error) {
    console.warn('Date formatting error:', error, 'Date value:', date);
    return fallback;
  }
}

/**
 * Safe locale time string formatting
 */
export function safeToLocaleTimeString(date: any, fallback: string = 'Time unavailable'): string {
  try {
    const validDate = createSafeDate(date);
    if (!validDate) {
      return fallback;
    }

    return validDate.toLocaleTimeString();
  } catch (error) {
    console.warn('Time formatting error:', error, 'Date value:', date);
    return fallback;
  }
}

/**
 * Safe ISO string formatting
 */
export function safeToISOString(date: any, fallback?: string): string | null {
  try {
    const validDate = createSafeDate(date);
    if (!validDate) {
      return fallback || null;
    }

    return validDate.toISOString();
  } catch (error) {
    console.warn('ISO string formatting error:', error, 'Date value:', date);
    return fallback || null;
  }
}

/**
 * Validates and sanitizes date objects in API responses
 * Recursively processes nested objects and arrays
 */
export function sanitizeDatesInObject<T>(obj: any): T {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => sanitizeDatesInObject(item)) as T;
  }

  if (typeof obj === 'object') {
    const sanitized: any = {};

    for (const [key, value] of Object.entries(obj)) {
      // Check if this looks like a date field
      if (typeof value === 'string' && (
        key.toLowerCase().includes('date') ||
        key.toLowerCase().includes('time') ||
        key.toLowerCase().includes('at') ||
        key === 'scheduledAt' ||
        key === 'startedAt' ||
        key === 'completedAt' ||
        key === 'lastUpdated' ||
        key === 'updatedAt' ||
        key === 'createdAt' ||
        key === 'lastCheck'
      )) {
        const safeDate = createSafeDate(value);
        sanitized[key] = safeDate || new Date(); // Fallback to current date
      } else if (typeof value === 'object') {
        sanitized[key] = sanitizeDatesInObject(value);
      } else {
        sanitized[key] = value;
      }
    }

    return sanitized as T;
  }

  return obj;
}

/**
 * Date comparison utilities with safety checks
 */
export function safeDateComparison(date1: any, date2: any): number {
  const d1 = createSafeDate(date1);
  const d2 = createSafeDate(date2);

  if (!d1 && !d2) return 0;
  if (!d1) return 1;
  if (!d2) return -1;

  return d1.getTime() - d2.getTime();
}

/**
 * Get current date with validation (should never fail)
 */
export function getCurrentDate(): Date {
  const now = new Date();
  if (isValidDate(now)) {
    return now;
  }

  // This should never happen, but just in case...
  console.error('System date is invalid!');
  return new Date(Date.now()); // Alternative method
}

/**
 * Mock data date generator for testing
 * Generates valid dates for development/testing scenarios
 */
export function generateMockDate(offsetDays: number = 0): Date {
  const now = getCurrentDate();
  const mockDate = new Date(now.getTime() + (offsetDays * 24 * 60 * 60 * 1000));

  // Ensure the generated date is valid
  if (isValidDate(mockDate)) {
    return mockDate;
  }

  // Fallback if calculation somehow fails
  return getCurrentDate();
}

/**
 * Common date validation errors for better debugging
 */
export const DateValidationErrors = {
  INVALID_DATE_OBJECT: 'Date object is invalid or not finite',
  INVALID_DATE_STRING: 'Date string cannot be parsed',
  NULL_OR_UNDEFINED: 'Date value is null or undefined',
  EMPTY_STRING: 'Date string is empty',
  FORMATTING_ERROR: 'Date formatting failed'
} as const;

/**
 * Comprehensive date validation with detailed error reporting
 */
export function validateDateWithDetails(value: any): {
  isValid: boolean;
  date: Date | null;
  error?: string;
} {
  if (value === null || value === undefined) {
    return {
      isValid: false,
      date: null,
      error: DateValidationErrors.NULL_OR_UNDEFINED
    };
  }

  if (typeof value === 'string' && value.trim() === '') {
    return {
      isValid: false,
      date: null,
      error: DateValidationErrors.EMPTY_STRING
    };
  }

  try {
    const date = new Date(value);

    if (!isValidDate(date)) {
      return {
        isValid: false,
        date: null,
        error: DateValidationErrors.INVALID_DATE_OBJECT
      };
    }

    return {
      isValid: true,
      date,
      error: undefined
    };

  } catch (error) {
    return {
      isValid: false,
      date: null,
      error: `${DateValidationErrors.INVALID_DATE_STRING}: ${error instanceof Error ? error.message : 'Unknown error'}`
    };
  }
}